import { isValidEmail } from "@/utils/isValidEmail";
import styled from "@emotion/styled";
import { FormField, Input, Pill, Variables, crossIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import { useCallback, useId, useState } from "react";

const splitEmails = (input: string): string[] =>
  input
    .split(/[,\s]+/)
    .map((token) => token.trim())
    .filter(Boolean);

interface EmailMultiInputProps {
  emails?: string[];
  onChange: (emails: string[]) => void;
  label: string;
}

export const PillContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Variables.space25};
  margin: ${Variables.space100} 0;
`;

export const EmailMultiInput = ({
  emails = [],
  onChange,
  label,
}: EmailMultiInputProps) => {
  const [draft, setDraft] = useState("");
  const [hasInvalidEmail, setHasInvalidEmail] = useState(false);
  const uniqueId = useId();

  const addEmails = useCallback(
    (rawInput: string) => {
      const tokens = splitEmails(rawInput);
      const validEmails = tokens.filter(
        (email) => isValidEmail(email) && !emails.includes(email)
      );
      const hasInvalid = tokens.some((token) => !isValidEmail(token));

      setHasInvalidEmail(hasInvalid);

      if (validEmails.length > 0) {
        onChange([...emails, ...validEmails]);
      }
    },
    [emails, onChange]
  );

  const commitDraft = useCallback(() => {
    if (!draft) {
      return;
    }

    addEmails(draft);
    setDraft("");
  }, [draft, addEmails]);

  const handleRemove = (email: string) => {
    onChange(emails.filter((item) => item !== email));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitDraft();
    }
  };

  return (
    <>
      <FormField
        shouldValidate={hasInvalidEmail}
        valid={!hasInvalidEmail}
        validation={{
          msg: t("label.invalid_email"),
          id: `${uniqueId}-email-validation-error`,
          type: "error",
        }}
        fieldHelper={{
          msg: t("prompt.add_email_helper"),
        }}
      >
        <Input
          id={`${uniqueId}-email-multi-input`}
          label={label}
          type="email"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setHasInvalidEmail(false);
          }}
          onBlur={commitDraft}
          onKeyDown={handleKeyDown}
        />
      </FormField>

      {emails.length > 0 && (
        <PillContainer>
          {emails.map((email) => (
            <Pill
              key={email}
              selected
              size="xsmall"
              label={email}
              trailingIcon={crossIcon}
              aria-label={t("action.remove_email", { email })}
              onClick={() => handleRemove(email)}
            />
          ))}
        </PillContainer>
      )}
    </>
  );
};
